import React, { useState, useCallback, useMemo } from "react"
import PropTypes from "prop-types"
import { Button, Flex, Input, Modal, Select, Tag, Typography } from "antd"
import { PlusOutlined, EditOutlined, DeleteOutlined, CloseOutlined } from "@ant-design/icons"
import { useTranslation } from "react-i18next"
import { useDispatch } from "react-redux"
import { createLabel, updateLabel, deleteLabel, getLabels } from "store/accounting"
import { toaster } from "components/common/Toast"
import warningIcon from "assets/icons/warningIcon.svg"
import IconWithBg from "ui/IconWithBg"

export default function TagSelector({ availableTags, selectedTags, onChange, disabled }) {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const [isManageModalOpen, setIsManageModalOpen] = useState(false)
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)
  const [isEditModalOpen, setIsEditModalOpen] = useState(false)
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const [newTagName, setNewTagName] = useState("")
  const [editingTag, setEditingTag] = useState(null)
  const [editTagName, setEditTagName] = useState("")
  const [deletingTag, setDeletingTag] = useState(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [createTagError, setCreateTagError] = useState("")
  const [editTagError, setEditTagError] = useState("")

  const tagOptions = useMemo(() => availableTags.map((tag) => ({ value: tag.id, label: tag.name })), [availableTags])

  const handleCreateTag = useCallback(async () => {
    if (!newTagName.trim()) return
    setIsSubmitting(true)
    try {
      const result = await dispatch(createLabel(newTagName.trim()))
      if (!result?.error) {
        dispatch(getLabels())
        toaster.success(t("expense.tags.created_success"))
        setNewTagName("")
        setCreateTagError("")
        setIsCreateModalOpen(false)
        if (result?.data?.id) {
          onChange([...selectedTags, result.data.id])
        }
      }
    } catch (err) {
      const nameErr = err?.data?.message?.name?.[0]
      const msg = nameErr ? `Name ${nameErr}` : err?.data?.error || t("expense.tags.create_error")
      setCreateTagError(msg)
    } finally {
      setIsSubmitting(false)
    }
  }, [dispatch, newTagName, t, selectedTags, onChange])

  const handleUpdateTag = useCallback(async () => {
    if (!editTagName.trim() || !editingTag) return
    setIsSubmitting(true)
    try {
      const result = await dispatch(updateLabel(editingTag.id, editTagName.trim()))
      if (!result?.error) {
        dispatch(getLabels())
        toaster.success(t("expense.tags.updated_success"))
        setEditingTag(null)
        setEditTagName("")
        setEditTagError("")
        setIsEditModalOpen(false)
      }
    } catch (err) {
      const nameErr = err?.data?.message?.name?.[0]
      const msg = nameErr ? `Name ${nameErr}` : err?.data?.error || t("expense.tags.create_error")
      setEditTagError(msg)
    } finally {
      setIsSubmitting(false)
    }
  }, [dispatch, editingTag, editTagName, t])

  const handleDeleteTag = useCallback(async () => {
    if (!deletingTag) return
    setIsSubmitting(true)
    try {
      const result = await dispatch(deleteLabel(deletingTag.id))
      if (!result?.error) {
        dispatch(getLabels())
        toaster.success(t("expense.tags.deleted_success"))
        onChange(selectedTags.filter((id) => id !== deletingTag.id))
        setDeletingTag(null)
        setIsDeleteModalOpen(false)
      }
    } finally {
      setIsSubmitting(false)
    }
  }, [dispatch, deletingTag, t, selectedTags, onChange])

  const openEditModal = useCallback((tag) => {
    setEditingTag(tag)
    setEditTagName(tag.name)
    setIsEditModalOpen(true)
    setIsManageModalOpen(false)
  }, [])

  const openDeleteModal = useCallback((tag) => {
    setDeletingTag(tag)
    setIsDeleteModalOpen(true)
    setIsManageModalOpen(false)
  }, [])

  return (
    <>
      <Flex vertical gap={6}>
        <Flex justify="space-between" align="center">
          <Typography.Text strong>{t("expense.tags.label")}</Typography.Text>
          <Button type="link" size="small" icon={<EditOutlined />} onClick={() => setIsManageModalOpen(true)} disabled={disabled}>
            {t("expense.tags.manage")}
          </Button>
        </Flex>
        <Select
          mode="multiple"
          showSearch
          filterOption={(input, option) => (option?.label ?? "").toLowerCase().includes(input.toLowerCase())}
          placeholder={t("expense.tags.select_placeholder")}
          value={selectedTags}
          onChange={onChange}
          disabled={disabled}
          style={{ width: "100%" }}
          options={tagOptions}
          dropdownRender={(menu) => (
            <>
              {menu}
              <Flex className="p-xs border-top">
                <Button type="link" icon={<PlusOutlined />} onClick={() => setIsCreateModalOpen(true)} block>
                  {t("expense.tags.create_new")}
                </Button>
              </Flex>
            </>
          )}
        />
      </Flex>

      <Modal
        open={isManageModalOpen}
        title={t("expense.tags.manage_title")}
        onCancel={() => setIsManageModalOpen(false)}
        footer={[
          <Button key="close" onClick={() => setIsManageModalOpen(false)}>
            {t("global.close")}
          </Button>,
          <Button
            key="create"
            color="primary"
            variant="solid"
            onClick={() => {
              setIsManageModalOpen(false)
              setIsCreateModalOpen(true)
            }}
          >
            {t("expense.tags.create_new")}
          </Button>
        ]}
        width={500}
      >
        <Flex vertical gap={8} className="py-s">
          {availableTags.length === 0 ? (
            <Typography.Text type="secondary">{t("expense.tags.no_tags")}</Typography.Text>
          ) : (
            availableTags.map((tag) => (
              <Flex key={tag.id} justify="space-between" align="center" className="p-xs border rounded">
                <Tag>{tag.name}</Tag>
                <Flex gap={8}>
                  <IconWithBg
                    icon={<EditOutlined style={{ color: "var(--color-primary)" }} />}
                    onClick={() => openEditModal(tag)}
                    aria-label={t("global.edit")}
                  />
                  <IconWithBg
                    icon={<DeleteOutlined style={{ color: "var(--color-error)" }} />}
                    onClick={() => openDeleteModal(tag)}
                    aria-label={t("global.delete")}
                  />
                </Flex>
              </Flex>
            ))
          )}
        </Flex>
      </Modal>

      <Modal
        open={isCreateModalOpen}
        title={t("expense.tags.create_title")}
        onCancel={() => {
          setIsCreateModalOpen(false)
          setNewTagName("")
          setCreateTagError("")
        }}
        footer={[
          <Button
            key="cancel"
            onClick={() => {
              setIsCreateModalOpen(false)
              setNewTagName("")
              setCreateTagError("")
            }}
          >
            {t("global.cancel")}
          </Button>,
          <Button key="create" type="primary" onClick={handleCreateTag} disabled={!newTagName.trim()} loading={isSubmitting}>
            {t("expense.tags.create")}
          </Button>
        ]}
      >
        <Flex vertical gap={8} className="py-s">
          <Typography.Text strong>{t("expense.tags.name_label")}</Typography.Text>
          <Input
            placeholder={t("expense.tags.name_placeholder")}
            value={newTagName}
            onChange={(e) => { setNewTagName(e.target.value); setCreateTagError("") }}
            onPressEnter={handleCreateTag}
            maxLength={50}
            status={createTagError ? "error" : ""}
          />
          {createTagError && <Typography.Text type="danger">{createTagError}</Typography.Text>}
        </Flex>
      </Modal>
      <Modal
        open={isEditModalOpen}
        title={t("expense.tags.edit_title")}
        onCancel={() => {
          setIsEditModalOpen(false)
          setEditingTag(null)
          setEditTagName("")
          setEditTagError("")
        }}
        footer={[
          <Button
            key="cancel"
            onClick={() => {
              setIsEditModalOpen(false)
              setEditingTag(null)
              setEditTagName("")
              setEditTagError("")
            }}
          >
            {t("global.cancel")}
          </Button>,
          <Button key="update" type="primary" onClick={handleUpdateTag} disabled={!editTagName.trim()} loading={isSubmitting}>
            {t("expense.tags.update")}
          </Button>
        ]}
      >
        <Flex vertical gap={8} className="py-s">
          <Typography.Text strong>{t("expense.tags.name_label")}</Typography.Text>
          <Input
            placeholder={t("expense.tags.name_placeholder")}
            value={editTagName}
            onChange={(e) => { setEditTagName(e.target.value); setEditTagError("") }}
            onPressEnter={handleUpdateTag}
            maxLength={50}
            status={editTagError ? "error" : ""}
          />
          {editTagError && <Typography.Text type="danger">{editTagError}</Typography.Text>}
        </Flex>
      </Modal>
      <Modal
        open={isDeleteModalOpen}
        onCancel={() => {
          setIsDeleteModalOpen(false)
          setDeletingTag(null)
        }}
        closable={false}
        centered
        maskClosable={false}
        footer={null}
        width={400}
      >
        <Button
          type="text"
          onClick={() => {
            setIsDeleteModalOpen(false)
            setDeletingTag(null)
          }}
          style={{ position: "absolute", top: 16, right: 16, padding: 0, color: "var(--color-text-secondary-light)" }}
        >
          <CloseOutlined />
        </Button>
        <Flex justify="center" className="mb-m">
          <Flex
            justify="center"
            align="center"
            style={{ backgroundColor: "var(--color-danger-surface, #fff1f0)", padding: 12, borderRadius: "50%" }}
          >
            <img src={warningIcon} alt="warning" width="24" height="24" />
          </Flex>
        </Flex>
        <h2
          style={{ fontSize: "var(--font-size-2xl)", fontWeight: 700, color: "var(--color-text-primary)", margin: "0 0 var(--spacing-s)" }}
        >
          {t("expense.tags.delete_title")}
        </h2>
        <p style={{ fontSize: "var(--font-size-m)", color: "var(--color-text-secondary)", margin: 0, lineHeight: 1.5 }}>
          {t("expense.tags.delete_confirmation", { name: deletingTag?.name })}
        </p>
        <Flex gap={8} style={{ marginTop: "var(--spacing-xl)" }}>
          <Button
            color="primary"
            variant="outlined"
            onClick={() => {
              setIsDeleteModalOpen(false)
              setDeletingTag(null)
            }}
            style={{ borderRadius: "var(--border-radius-xl)", paddingInline: "var(--spacing-xl)" }}
          >
            {t("global.cancel")}
          </Button>
          <Button
            type="primary"
            danger
            onClick={handleDeleteTag}
            loading={isSubmitting}
            style={{ borderRadius: "var(--border-radius-xl)", paddingInline: "var(--spacing-xl)" }}
          >
            {t("global.delete")}
          </Button>
        </Flex>
      </Modal>
    </>
  )
}

TagSelector.propTypes = {
  availableTags: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      name: PropTypes.string.isRequired
    })
  ).isRequired,
  selectedTags: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool
}

TagSelector.defaultProps = {
  selectedTags: [],
  disabled: false
}
